import pluralize from "pluralize";

import type {
  BreakpointBackgroundIconId,
  BreakpointIconId,
  ProgressBarBreakpointConfig,
  ProgressBarConfig,
  ProgressBarCurrencyFormatter,
  ProgressBarDeviceConfig,
} from "@intelligems/ig-types";

import { ProgressBarBreakpoint } from "../types";

export class BreakpointBuilder {
  protected readonly config: ProgressBarConfig;
  protected readonly device: ProgressBarDeviceConfig;
  protected readonly formatter: ProgressBarCurrencyFormatter;

  public constructor(
    config: ProgressBarConfig,
    device: ProgressBarDeviceConfig,
    formatter: ProgressBarCurrencyFormatter
  ) {
    this.config = config;
    this.device = device;
    this.formatter = formatter;
  }

  public build() {
    const breakpoints = this.getBreakpoints();
    for (const breakpoint of breakpoints) {
      this.expandBreakpoint(breakpoint);
    }
    return breakpoints;
  }

  public expandIncomplete(breakpoint: ProgressBarBreakpoint, value: number) {
    const difference = breakpoint.value - value;
    const quantity = this.getUnit(difference);
    const unitName = this.getUnitName(difference);
    breakpoint.incompleteText = this.replace(breakpoint.incompleteText, [
      ["quantity", quantity],
      ["unitName", unitName],
    ]);
  }

  getBreakpoints(): ProgressBarBreakpoint[] {
    const { breakpoints } = this.device;
    return breakpoints.map((b, index): ProgressBarBreakpoint => {
      const count = breakpoints.length;
      return {
        value: b.value,
        reward: b.reward,
        tooltipEnabled: b.tooltipEnabled ?? true,
        label: this.getBreakpointLabel(b),
        completeText: this.getBreakpointCompleteText(b, index, count),
        incompleteText: this.getBreakpointIncompleteText(b),
        active: {
          backgroundIconId: this.getActiveBackgroundIconId(b),
          iconId: this.getActiveIconId(b),
        },
        inactive: {
          backgroundIconId: this.getInactiveBackgroundIconId(b),
          iconId: this.getInactiveIconId(b),
        },
        index,
        progress: 0,
      };
    });
  }

  protected getBreakpointLabel(breakpoint: ProgressBarBreakpointConfig) {
    if (breakpoint.label) {
      return breakpoint.label;
    } else if (this.config.unitType === "dollar") {
      if (this.version === 1) {
        return "Spend #unit to get #rewardSnippet";
      } else {
        return "#rewardSnippet";
      }
    } else {
      if (this.version === 1) {
        return "Buy #unit #unitName to get #rewardSnippet";
      } else {
        return "#rewardSnippet";
      }
    }
  }

  protected getBreakpointCompleteText(
    breakpoint: ProgressBarBreakpointConfig,
    index: number,
    count: number
  ) {
    if (breakpoint.completeText) {
      return breakpoint.completeText;
    } else {
      if (this.version === 1) {
        return index < count - 1
          ? "You now have #rewardSnippet"
          : "Congratulations, you now have #rewardSnippet";
      } else {
        return "You now have #rewardSnippet";
      }
    }
  }

  protected getBreakpointIncompleteText(
    breakpoint: ProgressBarBreakpointConfig
  ) {
    if (breakpoint.incompleteText) {
      return breakpoint.incompleteText;
    } else if (this.config.unitType === "unit") {
      if (this.version === 1) {
        return "Buy #quantity #more #unitName to get #rewardSnippet";
      } else {
        return "Buy #quantity #more #unitName to get #rewardSnippet!";
      }
    } else {
      if (this.version === 1) {
        return "Spend #quantity to unlock #rewardSnippet";
      } else {
        return "Spend #quantity to get #rewardSnippet";
      }
    }
  }

  protected getActiveIconId(
    breakpoint: ProgressBarBreakpointConfig
  ): BreakpointIconId {
    return breakpoint.active.iconId ?? "check";
  }

  protected getInactiveIconId(
    breakpoint: ProgressBarBreakpointConfig
  ): BreakpointIconId {
    return breakpoint.inactive.iconId ?? "lockClosed";
  }

  protected getActiveBackgroundIconId(
    breakpoint: ProgressBarBreakpointConfig
  ): BreakpointBackgroundIconId {
    if (breakpoint.active.backgroundIconId) {
      return breakpoint.active.backgroundIconId;
    } else {
      return "circle";
    }
  }

  protected getInactiveBackgroundIconId(
    breakpoint: ProgressBarBreakpointConfig
  ) {
    if (breakpoint.inactive.backgroundIconId) {
      return breakpoint.inactive.backgroundIconId;
    } else {
      return "circle";
    }
  }

  protected expandBreakpoint(breakpoint: ProgressBarBreakpoint) {
    const unit = this.getUnit(breakpoint.value);
    const unitName = this.getUnitName(breakpoint.value);
    const rewardSnippet = this.getRewardSnippet(breakpoint);
    const labelRewardSnippet = this.getRewardSnippet(breakpoint, true);
    const discount = this.getDiscount(breakpoint);
    const more = this.getMore();

    breakpoint.label = this.replace(breakpoint.label, [
      ["rewardSnippet", labelRewardSnippet],
      ["rewardText", labelRewardSnippet],
      ["unitName", unitName],
      ["unit", unit],
      ["discount", discount],
    ]);

    breakpoint.completeText = this.replace(breakpoint.completeText, [
      ["rewardSnippet", rewardSnippet],
      ["rewardText", rewardSnippet],
      ["unitName", unitName],
      ["unit", unit],
      ["discount", discount],
    ]);

    breakpoint.incompleteText = this.replace(breakpoint.incompleteText, [
      ["rewardSnippet", rewardSnippet],
      ["rewardText", rewardSnippet],
      ["discount", discount],
      ["more", more],
    ]);
  }

  protected getUnitName(value: number) {
    const unitName =
      this.device.widget.variables?.textReplacements?.unitName || "item";
    return pluralize(unitName, value);
  }

  protected getUnit(value: number) {
    return this.config.unitType === "dollar"
      ? this.formatter(value)
      : value.toString();
  }

  protected getMore() {
    return this.device.widget.variables?.textReplacements?.more || "more";
  }

  protected getDiscount(breakpoint: ProgressBarBreakpoint) {
    if (breakpoint.reward.discount === undefined) {
      return "";
    }
    return this.config.discountType === "dollar"
      ? this.formatter(breakpoint.reward.discount)
      : breakpoint.reward.discount.toString() + "%";
  }

  protected getRewardSnippet(
    breakpoint: ProgressBarBreakpoint,
    isLabel: boolean = false
  ) {
    const snippets: string[] = [];
    if (breakpoint.reward.discount) {
      snippets.push(`#discount off`);
    }
    if (breakpoint.reward.freeShipping) {
      snippets.push(`free shipping`);
    }
    if (breakpoint.reward.gwp) {
      snippets.push(isLabel ? `free gift` : `a free gift`);
    }
    if (snippets.length === 0) {
      snippets.push(`#discount off`);
    }
    let text = "";
    if (snippets.length > 2) {
      const last = snippets.pop();
      text = snippets.join(",") + " and " + last;
    } else if (snippets.length > 0) {
      text = snippets.join(" & ");
    }
    if (isLabel && this.version === 2 && text.length > 0) {
      text = text.charAt(0).toUpperCase() + text.slice(1);
    }

    return text;
  }

  protected replace(source: string, replacements: [string, string][]) {
    let result = source;
    for (const replacement of replacements) {
      result = result
        .replaceAll("#" + replacement[0], replacement[1])
        .replaceAll(
          "#" + replacement[0].toUpperCase(),
          replacement[1].toUpperCase()
        );
    }
    return result;
  }

  protected get version() {
    switch (this.device.widget.variant) {
      case "progressBarWidget1":
      case "progressBarWidget2":
      case "progressBarWidget4":
        return 1;
      case "progressBar1":
      case "progressBar2":
      case "progressBar3":
        return 2;
      default:
        return 0;
    }
  }
}
